export interface SponsorshipPartner {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: 'supplements' | 'fitness' | 'wellness' | 'nutrition' | 'technology';
  partnership_level: 'platinum' | 'gold' | 'silver' | 'bronze';
  discount_percentage: number;
  referral_bonus: number;
  active_campaigns: number;
  total_referrals: number;
  rating: number;
  verified: boolean;
}

export interface SponsoredProduct {
  id: string;
  name: string;
  description: string;
  image: string;
  price: number;
  original_price: number;
  discount_percentage: number;
  sponsor_id: string;
  sponsor_name: string;
  category: string;
  rating: number;
  review_count: number;
  in_stock: boolean;
  shipping_info: string;
  tags: string[];
}

export interface SponsorCoupon {
  id: string;
  code: string;
  description: string;
  discount_type: 'percentage' | 'fixed' | 'free_shipping';
  discount_value: number;
  minimum_purchase: number;
  sponsor_id: string;
  sponsor_name: string;
  valid_until: string;
  usage_limit: number;
  used_count: number;
  active: boolean;
}

export interface SponsorInteraction {
  id: string;
  user_id: string;
  sponsor_id: string;
  interaction_type: 'click' | 'purchase' | 'referral' | 'review';
  product_id?: string;
  coupon_id?: string;
  amount?: number;
  commission_earned: number;
  timestamp: string;
  status: 'pending' | 'completed' | 'cancelled';
}

export const exampleSponsors: SponsorshipPartner[] = [
  {
    id: '1',
    name: 'VitaBoost Nutrition',
    logo: '/logos/vitaboost.png',
    description: 'Premium supplements for optimal health and performance',
    category: 'supplements',
    partnership_level: 'platinum',
    discount_percentage: 20,
    referral_bonus: 5,
    active_campaigns: 3,
    total_referrals: 1247,
    rating: 4.8,
    verified: true
  },
  {
    id: '2',
    name: 'FitTech Pro',
    logo: '/logos/fittech.png',
    description: 'Advanced fitness tracking and wellness technology',
    category: 'technology',
    partnership_level: 'gold',
    discount_percentage: 15,
    referral_bonus: 3,
    active_campaigns: 2,
    total_referrals: 892,
    rating: 4.6,
    verified: true
  },
  {
    id: '3',
    name: 'Organic Harvest',
    logo: '/logos/organic.png',
    description: 'Organic and natural wellness products',
    category: 'wellness',
    partnership_level: 'silver',
    discount_percentage: 10,
    referral_bonus: 2,
    active_campaigns: 1,
    total_referrals: 567,
    rating: 4.7,
    verified: true
  }
];

export const exampleSponsoredProducts: SponsoredProduct[] = [
  {
    id: '1',
    name: 'VitaBoost Omega-3 Premium',
    description: 'High-quality fish oil supplement with DHA and EPA',
    image: '/products/omega3.jpg',
    price: 29.99,
    original_price: 39.99,
    discount_percentage: 25,
    sponsor_id: '1',
    sponsor_name: 'VitaBoost Nutrition',
    category: 'supplements',
    rating: 4.8,
    review_count: 1247,
    in_stock: true,
    shipping_info: 'Free shipping on orders over $50',
    tags: ['omega-3', 'heart-health', 'brain-health']
  },
  {
    id: '2',
    name: 'FitTech Pro Smart Watch',
    description: 'Advanced fitness tracking with health monitoring',
    image: '/products/smartwatch.jpg',
    price: 199.99,
    original_price: 249.99,
    discount_percentage: 20,
    sponsor_id: '2',
    sponsor_name: 'FitTech Pro',
    category: 'technology',
    rating: 4.6,
    review_count: 892,
    in_stock: true,
    shipping_info: 'Free 2-day shipping',
    tags: ['fitness', 'tracking', 'smartwatch']
  },
  {
    id: '3',
    name: 'Organic Harvest Probiotics',
    description: 'Natural probiotic supplement for gut health',
    image: '/products/probiotics.jpg',
    price: 24.99,
    original_price: 29.99,
    discount_percentage: 17,
    sponsor_id: '3',
    sponsor_name: 'Organic Harvest',
    category: 'wellness',
    rating: 4.7,
    review_count: 567,
    in_stock: true,
    shipping_info: 'Free shipping',
    tags: ['probiotics', 'gut-health', 'organic']
  }
];

export const exampleCoupons: SponsorCoupon[] = [
  {
    id: '1',
    code: 'VITA20',
    description: '20% off all VitaBoost products',
    discount_type: 'percentage',
    discount_value: 20,
    minimum_purchase: 30,
    sponsor_id: '1',
    sponsor_name: 'VitaBoost Nutrition',
    valid_until: '2024-12-31',
    usage_limit: 1000,
    used_count: 234,
    active: true
  },
  {
    id: '2',
    code: 'FITTECH15',
    description: '15% off FitTech Pro devices',
    discount_type: 'percentage',
    discount_value: 15,
    minimum_purchase: 100,
    sponsor_id: '2',
    sponsor_name: 'FitTech Pro',
    valid_until: '2024-11-30',
    usage_limit: 500,
    used_count: 156,
    active: true
  },
  {
    id: '3',
    code: 'ORGANIC10',
    description: '10% off Organic Harvest products',
    discount_type: 'percentage',
    discount_value: 10,
    minimum_purchase: 25,
    sponsor_id: '3',
    sponsor_name: 'Organic Harvest',
    valid_until: '2024-10-31',
    usage_limit: 750,
    used_count: 89,
    active: true
  }
];

export const exampleInteractions: SponsorInteraction[] = [
  {
    id: '1',
    user_id: 'user1',
    sponsor_id: '1',
    interaction_type: 'purchase',
    product_id: '1',
    amount: 29.99,
    commission_earned: 1.50,
    timestamp: new Date().toISOString(),
    status: 'completed'
  },
  {
    id: '2',
    user_id: 'user2',
    sponsor_id: '2',
    interaction_type: 'click',
    commission_earned: 0.10,
    timestamp: new Date().toISOString(),
    status: 'completed'
  },
  {
    id: '3',
    user_id: 'user3',
    sponsor_id: '3',
    interaction_type: 'referral',
    commission_earned: 2.00,
    timestamp: new Date().toISOString(),
    status: 'pending'
  }
]; 